/**
 * useStickyFiltersBar Hook
 *
 * Manages sticky filter bar behavior including:
 * - Detecting when filters should become sticky
 * - Auto-collapse on scroll, auto-expand when returning to top
 * - Height measurement for placeholder sizing
 */

import React from "react";

import { tailwindBreakpoints } from "@/styles/design-tokens";

// Navbar height constants for sticky calculations
const MOBILE_NAVBAR_HEIGHT = 48;
const DESKTOP_NAVBAR_HEIGHT = 60;
const DESKTOP_BREAKPOINT = tailwindBreakpoints.lg;

export interface UseStickyFiltersBarOptions {
  /** Whether search is in progress (disables sticky behavior) */
  isSearching: boolean;
  /** Whether assistant is loading (disables sticky behavior) */
  isLoadingAssistant: boolean;
}

export interface UseStickyFiltersBarReturn {
  /** Whether the filters bar is currently sticky */
  isSticky: boolean;
  /** Whether the filters are expanded (not collapsed) */
  filtersExpanded: boolean;
  /** Set the expanded state manually */
  setFiltersExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  /** Height of the filters content (for placeholder) */
  filtersHeight: number;
  /** Ref to attach to the section that triggers sticky behavior */
  filtersSectionTopRef: React.RefObject<HTMLDivElement | null>;
  /** Ref to attach to the filters content for height measurement */
  filtersContentRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Hook for managing sticky filter bar behavior
 */
export function useStickyFiltersBar({
  isSearching,
  isLoadingAssistant,
}: UseStickyFiltersBarOptions): UseStickyFiltersBarReturn {
  // State
  const [filtersExpanded, setFiltersExpanded] = React.useState(true);
  const [isSticky, setIsSticky] = React.useState(false);
  const [filtersHeight, setFiltersHeight] = React.useState(0);

  // Refs
  const filtersSectionTopRef = React.useRef<HTMLDivElement>(null);
  const filtersContentRef = React.useRef<HTMLDivElement>(null);
  const wasStickyRef = React.useRef(false);

  // Measure filters height for placeholder
  React.useEffect(() => {
    if (filtersContentRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setFiltersHeight(entry.contentRect.height);
        }
      });

      resizeObserver.observe(filtersContentRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  // Clear sticky state when loading begins
  React.useEffect(() => {
    if (isSearching || isLoadingAssistant) {
      setIsSticky(false);
    }
  }, [isSearching, isLoadingAssistant]);

  // Sticky behavior when scrolling past filters
  React.useEffect(() => {
    const handleScroll = () => {
      if (!filtersSectionTopRef.current) return;

      // Don't enable sticky behavior during loading state
      if (isSearching || isLoadingAssistant) {
        setIsSticky(false);
        wasStickyRef.current = false;
        return;
      }

      const rect = filtersSectionTopRef.current.getBoundingClientRect();
      const navbarHeight =
        window.innerWidth < DESKTOP_BREAKPOINT ? MOBILE_NAVBAR_HEIGHT : DESKTOP_NAVBAR_HEIGHT;

      // If the natural position of filters would be above the navbar
      const shouldBeSticky = rect.top <= navbarHeight;
      const wasSticky = wasStickyRef.current;

      // Auto-collapse when becoming sticky, auto-expand when returning to top
      if (shouldBeSticky && !wasSticky) {
        setFiltersExpanded(false);
      } else if (!shouldBeSticky && wasSticky) {
        // Delay expand slightly to prevent layout jump
        setTimeout(() => setFiltersExpanded(true), 50);
      }

      wasStickyRef.current = shouldBeSticky;
      setIsSticky(shouldBeSticky);
    };

    // Run immediately on mount to set correct initial state
    requestAnimationFrame(handleScroll);

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isSearching, isLoadingAssistant]);

  return {
    isSticky,
    filtersExpanded,
    setFiltersExpanded,
    filtersHeight,
    filtersSectionTopRef,
    filtersContentRef,
  };
}
